import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiCrownSimpleLight, PiShieldCheckLight, PiUsersLight } from 'react-icons/pi';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import { Well } from '../../shared/brand/Field';
import { TYPE_BADGE_COLOR, capitalizeSection } from './userEditModel';

export interface PlanOptionCardProps {
  label: string;
  type: string;
  price: number | null;
  seats?: string;
  sections?: string[];
  loading: boolean;
  disabled: boolean;
  /** Already-translated button text. */
  buttonLabel: string;
  /** The recommended option - champagne-edged rather than filled. */
  highlight?: boolean;
  onClick: () => void;
}

const SECTIONS_SHOWN = 8;

/** One assignable plan in the picker - a sunken row inside the licence card. */
const PlanOptionCard: React.FC<PlanOptionCardProps> = ({
  label,
  type,
  price,
  seats,
  sections,
  loading,
  disabled,
  buttonLabel,
  highlight,
  onClick,
}) => {
  const { t } = useTranslation();
  const extraSections = (sections?.length ?? 0) - SECTIONS_SHOWN;

  return (
    <Well
      className={clsx(
        'flex items-center justify-between gap-3',
        highlight && 'border-champagne/40 bg-champagne/5'
      )}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        {highlight ? (
          <PiCrownSimpleLight className="h-4 w-4 shrink-0 text-champagne" />
        ) : (
          <PiShieldCheckLight className="h-4 w-4 shrink-0 text-fg-muted" />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-fg-strong">{label}</span>
            <Badge color={TYPE_BADGE_COLOR[type] || 'gray'}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </Badge>
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-fg-muted">
            {price != null && <span>€{price}/{t('mo')}</span>}
            {seats && (
              <span className="flex items-center gap-1">
                <PiUsersLight className="h-3 w-3" />
                {seats} {t('seats')}
              </span>
            )}
          </div>
          {sections && sections.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {sections.slice(0, SECTIONS_SHOWN).map((s) => (
                <span
                  key={s}
                  className="rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] text-fg-muted"
                >
                  {capitalizeSection(s)}
                </span>
              ))}
              {extraSections > 0 && (
                <span className="text-[10px] text-fg-muted">+{extraSections}</span>
              )}
            </div>
          )}
        </div>
      </div>
      <Button
        variant={highlight ? 'primary' : 'outline'}
        size="small"
        disabled={disabled}
        isLoading={loading}
        onClick={onClick}
      >
        {buttonLabel}
      </Button>
    </Well>
  );
};

export default PlanOptionCard;
